import React, { useEffect, useState } from 'react';
import { Shield } from 'lucide-react';
import { ApplicationFormData, ApplicationFormErrors } from '@/types/application';
import { agentApi } from '@/services/agentApi';

interface HealthWelfareAccommodationStepProps {
  formData: ApplicationFormData;
  errors: ApplicationFormErrors;
  updateFormData: (updates: Partial<ApplicationFormData>) => void;
  setErrors: (errors: ApplicationFormErrors) => void;
}

export const HealthWelfareAccommodationStep: React.FC<HealthWelfareAccommodationStepProps> = ({
  formData,
  errors,
  updateFormData,
  setErrors,
}) => {
  const [selectedInstitution, setSelectedInstitution] = useState<any | null>(null);

  useEffect(() => {
    if (formData.universityId) {
      loadInstitutionInfo();
    } else {
      setSelectedInstitution(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.universityId]);

  const loadInstitutionInfo = async () => {
    try {
      const institutions = await agentApi.getApprovedInstitutions();
      const institution = institutions.find((inst: any) => inst.id === formData.universityId);
      setSelectedInstitution(institution || null);
    } catch (error) {
      console.error('Error loading institution info:', error);
      setSelectedInstitution(null);
    }
  };

  const handleChange = (field: keyof ApplicationFormData, value: any) => {
    // Only one insurance option can be selected at a time – behave like radio buttons
    if (field === 'insuranceViaSouthernCross' && value) {
      updateFormData({
        insuranceViaSouthernCross: true,
        insuranceViaUniCare: false,
        insuranceSelfArranged: false,
      });
    } else if (field === 'insuranceViaUniCare' && value) {
      updateFormData({
        insuranceViaSouthernCross: false,
        insuranceViaUniCare: true,
        insuranceSelfArranged: false,
      });
    } else if (field === 'insuranceSelfArranged' && value) {
      updateFormData({
        insuranceViaSouthernCross: false,
        insuranceViaUniCare: false,
        insuranceSelfArranged: true,
      });
    } else {
      // Allow unchecking to clear all if desired
      updateFormData({ [field]: value });
    }
    if (errors[field]) {
      setErrors({ ...errors, [field]: undefined });
    }
  };

  const institutionName = selectedInstitution?.name || 'the institution';
  const institutionCountry = selectedInstitution?.country || 'New Zealand';

  return (
    <div>
      <h5 className="fw-bold mb-3">F. Health, Welfare and Accommodation</h5>

      <div className="card border mb-3">
        <div className="card-header bg-light d-flex align-items-center">
          <Shield size={18} className="me-2 text-primary" />
          <h6 className="mb-0 fw-semibold">
            Insurance <span className="text-danger">*</span>
          </h6>
        </div>
        <div className="card-body">
          <p className="mb-3">
            Travel Insurance is compulsory for all international students studying in {institutionCountry}. This can be
            arranged by {institutionName} at your request through our travel insurance provider Southern Cross, or
            alternative providers.
          </p>

          <div className="mb-3">
            <div className="form-check d-flex align-items-center">
              <input
                className="form-check-input"
                type="checkbox"
                id="insuranceViaSouthernCross"
                checked={formData.insuranceViaSouthernCross || false}
                onChange={(e) => handleChange('insuranceViaSouthernCross', e.target.checked)}
              />
              <label className="form-check-label ms-2" htmlFor="insuranceViaSouthernCross">
                
                If you would like {institutionName} to arrange insurance with Southern Cross on your behalf tick here
              </label>
            </div>
          </div>

          <div className="mb-3">
            <div className="form-check d-flex align-items-center">
              <input
                className="form-check-input"
                type="checkbox"
                id="insuranceViaUniCare"
                checked={formData.insuranceViaUniCare || false}
                onChange={(e) => handleChange('insuranceViaUniCare', e.target.checked)}
              />
              <label className="form-check-label ms-2" htmlFor="insuranceViaUniCare">
                If you would like {institutionName} to arrange insurance with Uni-care on your behalf tick here
              </label>
            </div>
          </div>

          <div>
            <div className="form-check d-flex align-items-center">
              <input
                className="form-check-input"
                type="checkbox"
                id="insuranceSelfArranged"
                checked={formData.insuranceSelfArranged || false}
                onChange={(e) => handleChange('insuranceSelfArranged', e.target.checked)}
              />
              <label className="form-check-label ms-2" htmlFor="insuranceSelfArranged">
                Should you wish to organise it yourself proof of this must be produced prior to arrival into{' '}
                {institutionCountry}.
              </label>
            </div>
          </div>
          {errors.insuranceViaSouthernCross && (
            <div className="text-danger small mt-2">{errors.insuranceViaSouthernCross}</div>
          )}
        </div>
      </div>
    </div>
  );
};

